import { useRef, useState } from "react";
import { BedDouble, CalendarDays, Car, Check, Compass, GripVertical, MapPin, Sparkles, Sun, Trash2, User, Users, Utensils } from "lucide-react";
import { DualPrice } from "@/components/DualPrice";
import { splitPriceDay, sumIsk, fmtIsk, fmtUsdApprox, fmtEurApprox, ISK_PER_USD, ISK_PER_EUR, hotelPriceForTotal, isZeroPrice, evalMoneyExpr, moneyToIsk, nameKey } from "@/lib/price";

export const ITEM_TYPES = [
  { value: "activity", label: "Activity" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "private_event", label: "Private Event" },
  { value: "hotel", label: "Hotel" },
  { value: "transport", label: "Transport" },
];

const TYPE_META = {
  activity: { label: "Activity", Icon: Compass },
  lunch: { label: "Lunch", Icon: Sun },
  dinner: { label: "Dinner", Icon: Utensils },
  private_event: { label: "Private Event", Icon: Sparkles },
  hotel: { label: "Hotel", Icon: BedDouble },
  transport: { label: "Transport", Icon: Car },
};

export const dayHeading = (trip, day) => {
  if (!trip.arrival_date) return `Day ${day}`;
  const d = new Date(`${trip.arrival_date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return `Day ${day}`;
  d.setDate(d.getDate() + (day - 1));
  return `Day ${day} — ${d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}`;
};

const dayISODate = (trip, day) => {
  if (!trip.arrival_date) return null;
  const d = new Date(`${trip.arrival_date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + (day - 1));
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const staysOnNight = (trip, day) => {
  const iso = dayISODate(trip, day);
  if (!iso) return [];
  return (trip.hotels || []).filter((h) => h.check_in && h.check_out && h.check_in <= iso && iso < h.check_out);
};

// Every night of a stay shows the hotel + that night's price as a line in the schedule.
// The arrival day already carries the price on its check-in line, so only middle nights get
// one — placed just before dinner, and skipped entirely when the owner wrote an
// "arrival to hotel" line themselves (a day where a tour runs late). Price per night =
// the stay's total from the stays section divided by its nights.
const hotelDayLines = (trip, day, dayItems) => {
  const iso = dayISODate(trip, day);
  if (!iso) return [];
  if (dayItems.some((x) => /arriv/i.test(x.title || "") && /hotel/i.test(x.title || ""))) return [];
  return (trip.hotels || [])
    .filter((h) => {
      if (!h.check_in || !h.check_out) return false;
      if (h.ops_full || h.booked_directly || h.ops_client_books || h.ops_person) return false;
      if (!(h.check_in < iso && iso < h.check_out)) return false; // middle nights only
      // a hand-written hotel line already stands for this night — it carries the share itself
      const hn = nameKey(h.name);
      const first = hn.split(/\s+/)[0];
      if (hn && dayItems.some((x) => x.type === "hotel" && (nameKey(x.title).includes(hn) || (first.length > 3 && nameKey(x.title).includes(first))))) return false;
      return true;
    })
    .map((h) => {
      const nights = Math.max(
        1,
        Math.round((new Date(`${h.check_out}T00:00:00`) - new Date(`${h.check_in}T00:00:00`)) / 86400000) || parseInt(h.nights, 10) || 1
      );
      const total = moneyToIsk(hotelPriceForTotal(h));
      if (!total) return null;
      return { id: `hotelday-${h.hotel_id || h.name}-${day}`, name: h.name, share: Math.round(total / nights) };
    })
    .filter(Boolean);
};

// Vehicle rentals span whole days — entered once with a total price and a day range;
// every covered day shows the vehicle with its daily share at the top of the day.
const rentalDayLines = (trip, day) =>
  (trip.rentals || [])
    .filter((r) => r.name && (r.from_day || 1) <= day && day <= (r.to_day || r.from_day || 1))
    .map((r) => {
      const span = Math.max(1, (r.to_day || 1) - (r.from_day || 1) + 1);
      const total = moneyToIsk(r.price);
      if (!total) return null;
      return { id: `rentalday-${r.rental_id || r.name}-${day}`, name: r.name, share: Math.round(total / span) };
    })
    .filter(Boolean);

export const gapNights = (trip) => {
  if (!trip.arrival_date || !trip.departure_date) return [];
  const out = [];
  const d = new Date(`${trip.arrival_date}T00:00:00`);
  const dep = new Date(`${trip.departure_date}T00:00:00`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(dep.getTime())) return [];
  const pad = (n) => String(n).padStart(2, "0");
  while (d < dep) {
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const covered = (trip.hotels || []).some((h) => h.check_in && h.check_out && h.check_in <= iso && iso < h.check_out);
    if (!covered) out.push(iso);
    d.setDate(d.getDate() + 1);
  }
  return out;
};

export const formatGap = (trip, iso) => {
  const dayNum = Math.round((new Date(`${iso}T00:00:00`) - new Date(`${trip.arrival_date}T00:00:00`)) / 86400000) + 1;
  const d = new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return `Day ${dayNum} (${d})`;
};

export const TripView = ({ trip, renderItemAction, showClientMarks = false, hideHeader = false, daily = [], onMove, onDelete, onEdit, showGapWarnings = false, onRoomPick = null, onAcceptAlt = null, onMenuPick = null }) => {
  // fully booked = not available — it leaves the itinerary everywhere (demo, client page, preview)
  trip = { ...trip, items: (trip.items || []).filter((i) => !i.ops_full), hotels: (trip.hotels || []).filter((h) => !h.ops_full) };
  const region = trip.client_region;
  const dailyByDay = Object.fromEntries((daily || []).map((d) => [d.day, d]));
  const [dragId, setDragId] = useState(null);
  const dragIdRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [callFor, setCallFor] = useState(null); // item_id whose driver phone chooser (call / WhatsApp) is open
  const commitEdit = (itemId, field, value) => {
    setEditing(null);
    // price boxes act as calculators — "20.000*4" stores as 80.000 so every view and total agrees
    const v = field === "price" ? (evalMoneyExpr(value) ?? value.trim()) : value.trim();
    if (onEdit && (v || field === "price" || field === "details")) onEdit(itemId, field, v);
  };
  const gaps =
    showGapWarnings && (trip.hotels || []).some((h) => h.check_in && h.check_out) ? gapNights(trip) : [];
  // alternatives offered on fully-booked stays — the backend passes them through for client
  // payloads (night_alternatives); the demo/owner view reads them straight off the raw stays
  const nightAlts =
    trip.night_alternatives ||
    (trip.hotels || []).filter((h) => h.ops_full && (h.full_alternative || h.alternative)).map((h) => h.full_alternative || h.alternative);
  const days = [
    ...new Set([
      ...(trip.items || []).flatMap((i) => {
        const e = i.end_day > i.day ? i.end_day : i.day;
        return Array.from({ length: e - i.day + 1 }, (_, k) => i.day + k);
      }),
      // a rental's covered days exist even when nothing else is booked on them yet
      ...(trip.rentals || []).flatMap((r) => {
        const f = r.from_day || 1;
        const t = r.to_day || f;
        return Array.from({ length: t - f + 1 }, (_, k) => f + k);
      }),
    ]),
  ].sort((a, b) => a - b);
  return (
    <div>
      {!hideHeader && (
        <div className="text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">
            Prepared exclusively for
          </p>
          <h2 className="mt-3 font-luxe text-2xl uppercase leading-[1.2] tracking-[0.12em] text-neutral-50 sm:text-3xl">
            {trip.client?.name || "Your party"}
          </h2>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
            {(trip.arrival_date || trip.departure_date) && (
              <span className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-amber-400/70" />
                {trip.arrival_date || "?"}{trip.arrival_time ? ` · ${trip.arrival_time}` : ""} → {trip.departure_date || "?"}{trip.departure_time ? ` · ${trip.departure_time}` : ""}
              </span>
            )}
            {trip.pax && (
              <span className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                <Users className="h-3.5 w-3.5 text-amber-400/70" /> {trip.pax} guests
              </span>
            )}
          </div>
        </div>
      )}

      {trip.hotels?.length > 0 && (
        <div data-testid="stays-box" className="mt-10 border border-amber-500/35 bg-[#101016] p-1.5">
          <div className="border border-amber-500/15 p-5 sm:p-6">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
              <BedDouble className="h-3.5 w-3.5" /> Your Stays
            </p>
            <div className="mt-4 space-y-3">
              {trip.hotels.map((h, i) => {
                const ci = h.check_in;
                const co = h.check_out;
                let nights = h.nights;
                if (ci && co) {
                  const n = Math.round((new Date(`${co}T00:00:00`) - new Date(`${ci}T00:00:00`)) / 86400000);
                  if (n > 0) nights = String(n);
                }
                const dayNum =
                  ci && trip.arrival_date
                    ? Math.round((new Date(`${ci}T00:00:00`) - new Date(`${trip.arrival_date}T00:00:00`)) / 86400000) + 1
                    : null;
                const fmtD = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const notesDup = ci && /^\s*check[- ]?in/i.test(h.notes || "");
                const hBooked = !!(h.booked_directly || h.ops_client_books || h.ops_person);
                const meta = [
                  nights ? `${nights} night${Number(nights) > 1 ? "s" : ""}` : "",
                  h.rooms ? `${h.rooms} × ${h.room_type === "twin" ? "Twin" : "Double"}` : "",
                  ci ? `Check-in ${fmtD(ci)}${dayNum ? ` (Day ${dayNum})` : ""}` : "",
                  co ? `Check-out ${fmtD(co)}` : "",
                  notesDup ? "" : h.notes,
                ]
                  .filter(Boolean)
                .join(" · ");
                return (
                  <div
                    key={i}
                    data-testid={`trip-stay-${i}`}
                    className="border border-neutral-800 bg-[#0c0c10] px-4 py-3"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <p className="font-serif text-lg text-neutral-100">{h.name}</p>
                      <p data-testid={`trip-stay-meta-${i}`} className="text-[11px] uppercase tracking-[0.18em] text-amber-200/80">
                        {meta}
                        {hBooked ? (
                          <span data-testid={`stay-booked-directly-${i}`} className={`${meta ? "ml-2 " : ""}normal-case italic tracking-normal text-neutral-400`}>Client booked directly</span>
                        ) : (
                          <>
                            {meta && h.price && !isZeroPrice(h.price) ? " · " : ""}
                            {h.price && !isZeroPrice(h.price) && <DualPrice value={h.price} region={region} />}
                          </>
                        )}
                      </p>
                    </div>
                    {(h.full_alternative || (h.ops_full && h.alternative)) && (
                      <div className="mt-2">
                        <p data-testid={`stay-alternative-${i}`} className="font-serif text-sm italic text-amber-200/80">
                          Fully booked — proposed alternative: {h.full_alternative || h.alternative}
                        </p>
                        {onAcceptAlt && (
                          <button
                            data-testid={`accept-alt-hotel-${h.hotel_id || String(i)}`}
                            onClick={() => onAcceptAlt("hotel", h.hotel_id || String(i))}
                            className="mt-2 border border-amber-500/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                          >
                            Accept this alternative
                          </button>
                        )}
                      </div>
                    )}
                    {h.alt_accepted && (
                      <p data-testid={`stay-alt-accepted-${i}`} className="mt-2 font-serif text-sm italic text-emerald-300/80">
                        Alternative accepted — thank you.
                      </p>
                    )}
                    {!hBooked && (h.room_options || []).length > 0 && (                      <div className="mt-3 border-t border-amber-500/10 pt-3">
                        {onRoomPick && (
                          <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Room options — tick the one you prefer
                          </p>
                        )}
                        <div className="space-y-1.5">
                          {h.room_options.map((o) => {
                            const hKey = h.hotel_id || String(i);
                            const picked = h.picked_option === o.option_id;
                            const inner = (
                              <>
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center border transition-colors ${
                                    picked ? "border-amber-400 bg-amber-500 text-black" : "border-neutral-600"
                                  }`}
                                >
                                  {picked && <Check className="h-3 w-3" strokeWidth={3.5} />}
                                </span>
                                <span className={`flex-1 text-sm ${picked ? "text-amber-100" : "text-neutral-300"}`}>{o.label}</span>
                                <DualPrice value={o.price} region={region} className="text-[11px] uppercase tracking-[0.18em] text-amber-200/80" />
                              </>
                            );
                            return onRoomPick ? (
                              <button
                                key={o.option_id}
                                type="button"
                                data-testid={`room-pick-${hKey}-${o.option_id}`}
                                aria-pressed={picked}
                                onClick={() => onRoomPick(hKey, picked ? "" : o.option_id)}
                                className="flex w-full items-center gap-3 border border-neutral-800/80 px-3 py-2 text-left transition-colors hover:border-amber-500/40"
                              >
                                {inner}
                              </button>
                            ) : (
                              <div key={o.option_id} data-testid={`room-option-${hKey}-${o.option_id}`} className="flex items-center gap-3 px-3 py-1.5">
                                {inner}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {days.length === 0 && (
        <p className="mt-10 border border-dashed border-neutral-700 p-6 text-center text-sm text-neutral-500">
          Itinerary moments will appear here.
        </p>
      )}

      {gaps.length > 0 && (
        <div data-testid="stay-gap-warning" className="mt-6 border border-dashed border-red-500/50 bg-red-950/10 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-red-300/90">
            Unbooked nights — no stay assigned
          </p>
          <p className="mt-2 text-xs leading-relaxed tracking-wide text-red-200/80">
            {gaps.map((iso) => formatGap(trip, iso)).join("  ·  ")}
          </p>
          {nightAlts.length > 0 && (
            <p data-testid="gap-alternatives" className="mt-3 border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed tracking-wide text-amber-200/90">
              Suggested for these nights: <span className="font-serif text-sm">{nightAlts.join(" · ")}</span>
              {onAcceptAlt ? " — tap the stay's proposal to confirm it." : " — say the word and it is confirmed."}
            </p>
          )}
        </div>
      )}

      {days.map((day) => (
        <div
          key={day}
          className="mt-10"
          onDragOver={onMove ? (e) => e.preventDefault() : undefined}
          onDrop={
            onMove
              ? (e) => {
                  e.preventDefault();
                  if (dragIdRef.current) onMove(dragIdRef.current, day, null);
                  dragIdRef.current = null;
                  setDragId(null);
                }
              : undefined
          }
        >
          <div className="gold-hairline mb-4 w-full" />
          <h4 className="font-serif text-2xl font-light italic tracking-wide text-amber-200">{dayHeading(trip, day)}</h4>
          {staysOnNight(trip, day).map((h, i) => (
            <p key={i} data-testid={`night-at-${day}-${i}`} className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500">
              <BedDouble className="h-3.5 w-3.5 text-amber-400/60" /> Night at {h.name}
            </p>
          ))}
          {dailyByDay[day] && (
            <p data-testid={`daily-${day}`} className="mt-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500">
              {dailyByDay[day].sunrise && `Sunrise ${dailyByDay[day].sunrise} · Sunset ${dailyByDay[day].sunset}`}
              {dailyByDay[day].condition
                ? ` · ${dailyByDay[day].condition} · ${Math.round(dailyByDay[day].temp_min)}° / ${Math.round(dailyByDay[day].temp_max)}°${
                    dailyByDay[day].precip ? ` · ${dailyByDay[day].precip}% precip` : ""
                  }`
                : ""}
            </p>
          )}
          <div className="mt-4 space-y-4">
            {(() => {
              const dayItems = (trip.items || []).filter((i) => i.day <= day && (i.end_day > i.day ? i.end_day : i.day) >= day);
              const hLines = hotelDayLines(trip, day, dayItems).map((v) => ({ __hotelLine: true, ...v }));
              const rLines = rentalDayLines(trip, day).map((v) => ({ __rentalLine: true, ...v }));
              const dinnerIdx = dayItems.findIndex((x) => x.type === "dinner");
              const insertAt = dinnerIdx >= 0 ? dinnerIdx : dayItems.length; // hotel line sits just before dinner
              const renderList = [...rLines, ...dayItems.slice(0, insertAt), ...hLines, ...dayItems.slice(insertAt)]; // the rental leads the day
              return renderList.map((item) => {
                if (item.__rentalLine) {
                  return (
                    <div key={item.id} data-testid={item.id} className="border border-amber-500/25 bg-amber-500/[0.05] p-4 sm:p-5">
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-300/70">
                        <Car className="h-3.5 w-3.5" /> Rental
                      </span>
                      <p className="mt-1.5 font-serif text-lg font-light leading-snug text-neutral-50">Rental — {item.name}</p>
                      <p className="mt-2 font-luxe text-base tracking-[0.12em] text-amber-300">
                        <DualPrice value={fmtIsk(item.share)} region={region} /> <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">/ day</span>
                      </p>
                    </div>
                  );
                }
                if (item.__hotelLine) {
                  return (
                    <div key={item.id} data-testid={item.id} className="border border-amber-500/25 bg-amber-500/[0.05] p-4 sm:p-5">
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-300/70">
                        <BedDouble className="h-3.5 w-3.5" /> Hotel
                      </span>
                      <p className="mt-1.5 font-serif text-lg font-light leading-snug text-neutral-50">Hotel — {item.name}</p>
                      <p className="mt-2 font-luxe text-base tracking-[0.12em] text-amber-300">
                        <DualPrice value={fmtIsk(item.share)} region={region} /> <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">/ night</span>
                      </p>
                    </div>
                  );
                }
                const meta = TYPE_META[item.type] || TYPE_META.activity;
                const marked = showClientMarks && item.client_action;
                const endDay = item.end_day > item.day ? item.end_day : item.day;
                const charged = endDay - item.day; // return day is free (24h rule)
                const bookedDirectly = !!(item.booked_directly || item.ops_client_books || item.ops_person);
                // "Just info" rows never carry money — no price shown, no Add price prompt;
                // to price the line the owner unchecks Info first
                let shownPrice = bookedDirectly || item.ops_info
                  ? ""
                  : item.split_price && charged > 0
                    ? day < endDay
                      ? splitPriceDay(item.price, charged, day - item.day + 1)
                      : ""
                    : day === item.day && !isZeroPrice(item.price)
                      ? item.price
                      : "";
                // a stay-linked hotel line shows the stay's money — stored once, on the stay.
                // non-split stay: the check-in line carries the full price (unless another line
                // on that day already shows it). split across nights: every stay line shows only
                // its own night's share, so the days visibly add up to the stay's total.
                // matched by TITLE, not the auto marker; accents ignored ("Kría Hotel" = "Kría Hótel")
                const isStayLine = item.type === "hotel";
                if (!shownPrice && day === item.day && isStayLine) {
                  const hotelsArr = trip.hotels || [];
                  let stay = null;
                  const autoM = (item.auto || "").match(/^stay-(\d+)-in$/);
                  if (autoM) {
                    const cand = hotelsArr[Number(autoM[1])];
                    if (cand && nameKey(item.title).includes(nameKey(cand.name).slice(0, 12))) stay = cand;
                  }
                  if (!stay) {
                    const subject = nameKey((item.title || "").replace(/^check[- ]?(in|out)\b[:—–-]?\s*/i, ""));
                    stay = hotelsArr.find((h) => {
                      const hn = nameKey(h.name);
                      return hn && subject && (hn.includes(subject) || subject.includes(hn));
                    });
                  }
                  if (stay && !(stay.booked_directly || stay.ops_client_books || stay.ops_person || stay.ops_full)) {
                    const sp = hotelPriceForTotal(stay);
                    if (sp && !isZeroPrice(sp)) {
                      const iso = dayISODate(trip, day);
                      const nights =
                        stay.check_in && stay.check_out
                          ? Math.max(1, Math.round((new Date(`${stay.check_out}T00:00:00`) - new Date(`${stay.check_in}T00:00:00`)) / 86400000) || parseInt(stay.nights, 10) || 1)
                          : 0;
                      if (stay.split_price && nights > 0 && iso && stay.check_in <= iso && iso < stay.check_out) {
                        const nightNo = Math.round((new Date(`${iso}T00:00:00`) - new Date(`${stay.check_in}T00:00:00`)) / 86400000) + 1;
                        shownPrice = splitPriceDay(sp, nights, nightNo);
                      } else if (/^check[- ]?in\b/i.test((item.title || "").trim())) {
                        const pIsk = moneyToIsk(sp);
                        const elsewhere = (trip.items || []).some(
                          (x) => x.item_id !== item.item_id && x.type !== "hotel" && x.day === item.day && !isZeroPrice(x.price) && moneyToIsk(x.price) === pIsk
                        );
                        if (!elsewhere) shownPrice = sp;
                      }
                    }
                  }
                }
                const canEditHere = onEdit && item.item_id && day === item.day;
                return (
                  <div
                    key={item.item_id || item.title}
                    data-testid={`trip-item-${item.item_id || "row"}`}
                    draggable={!!(onMove && item.item_id)}
                    onDragStart={
                      onMove
                        ? (e) => {
                            dragIdRef.current = item.item_id;
                            setDragId(item.item_id);
                            e.dataTransfer.effectAllowed = "move";
                          }
                        : undefined
                    }
                    onDragOver={onMove ? (e) => { e.preventDefault(); e.stopPropagation(); } : undefined}
                    onDrop={
                      onMove
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (dragIdRef.current && dragIdRef.current !== item.item_id)
                              onMove(dragIdRef.current, day, item.item_id);
                            dragIdRef.current = null;
                            setDragId(null);
                          }
                        : undefined
                    }
                    className={`border p-4 sm:p-5 ${onMove && item.item_id ? "cursor-grab active:cursor-grabbing" : ""} ${
                      dragId === item.item_id ? "opacity-40" : ""
                    } ${
                      marked && item.client_action === "removed"
                        ? "border-red-900/50 bg-red-950/10"
                        : marked && item.client_action === "alternative"
                          ? "border-amber-500/40 bg-amber-500/5"
                          : "border-amber-500/15 bg-[#0d0d12]"
                    }`}
                  >
                    {onMove && item.item_id && (
                      <GripVertical className="float-right mt-1 h-4 w-4 text-neutral-700" />
                    )}
                    {onDelete && item.item_id && (
                      <button
                        data-testid={`demo-delete-${item.item_id}`}
                        onClick={() => onDelete(item.item_id)}
                        className="float-right ml-2 mt-1 text-neutral-700 transition-colors hover:text-red-400"
                        aria-label="Remove this moment"
                        title="Remove from schedule"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      {onEdit && item.item_id && editing?.id === item.item_id && editing.field === "time" ? (
                        <input
                          autoFocus
                          data-testid={`demo-edit-time-${item.item_id}`}
                          defaultValue={item.time}
                          onBlur={(e) => commitEdit(item.item_id, "time", e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitEdit(item.item_id, "time", e.currentTarget.value);
                            if (e.key === "Escape") setEditing(null);
                          }}
                          className="w-20 border border-amber-400/60 bg-[#0d0d12] px-2.5 py-1 font-luxe text-sm tracking-[0.15em] text-amber-200 outline-none"
                        />
                      ) : (
                        item.time || onEdit ? (
                          <span
                            data-testid={`trip-time-${item.item_id || "row"}`}
                            onClick={onEdit && item.item_id ? () => setEditing({ id: item.item_id, field: "time" }) : undefined}
                            title={onEdit && item.item_id ? "Click to edit the time" : undefined}
                            className={`border border-amber-400/60 bg-amber-500/15 px-2.5 py-1 font-luxe text-sm tracking-[0.15em] text-amber-200 ${onEdit && item.item_id ? "cursor-text transition-colors hover:bg-amber-500/30" : ""}`}
                          >
                            {item.time || (onEdit ? "--:--" : "")}
                          </span>
                        ) : null
                      )}
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">
                        <meta.Icon className="h-3.5 w-3.5" />
                        {meta.label}
                      </span>
                    </div>
                    {onEdit && item.item_id && editing?.id === item.item_id && editing.field === "title" ? (
                      <input
                        autoFocus
                        data-testid={`demo-edit-title-${item.item_id}`}
                        defaultValue={item.title}
                        onBlur={(e) => commitEdit(item.item_id, "title", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitEdit(item.item_id, "title", e.currentTarget.value);
                          if (e.key === "Escape") setEditing(null);
                        }}
                        className="mt-2 w-full border border-amber-500/40 bg-[#0d0d12] px-2 py-1 font-serif text-xl text-neutral-50 outline-none"
                      />
                    ) : (
                      <h5
                        data-testid={onEdit && item.item_id ? `demo-title-${item.item_id}` : undefined}
                        onClick={onEdit && item.item_id ? () => setEditing({ id: item.item_id, field: "title" }) : undefined}
                        title={onEdit && item.item_id ? "Click to edit the title" : undefined}
                        className={`mt-2 font-serif text-xl text-neutral-50 ${onEdit && item.item_id ? "cursor-text transition-colors hover:text-amber-100" : ""}`}
                      >
                        {item.title}
                      </h5>
                    )}
                    {onEdit && item.item_id && editing?.id === item.item_id && editing.field === "details" ? (
                      <textarea
                        autoFocus
                        rows={2}
                        data-testid={`demo-edit-details-${item.item_id}`}
                        defaultValue={item.details}
                        placeholder="A line or two — what makes this moment special"
                        onBlur={(e) => commitEdit(item.item_id, "details", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") setEditing(null);
                        }}
                        className="mt-1.5 w-full border border-amber-500/40 bg-[#0d0d12] px-2 py-1 text-sm leading-relaxed text-neutral-300 outline-none"
                      />
                    ) : (
                      item.details || onEdit ? (
                        <p
                          data-testid={`demo-details-${item.item_id || "row"}`}
                          onClick={onEdit && item.item_id ? () => setEditing({ id: item.item_id, field: "details" }) : undefined}
                          title={onEdit && item.item_id ? "Click to write a description" : undefined}
                          className={`mt-1.5 text-sm leading-relaxed text-neutral-400 ${onEdit && item.item_id ? "cursor-text transition-colors hover:text-neutral-200" : ""}`}
                        >
                          {item.details || (onEdit ? "Add a description…" : "")}
                        </p>
                      ) : null
                    )}
                    {item.type === "transport" && (item.pickup_loc || item.dropoff_loc || item.end_time) && (
                      <p data-testid={`trip-route-${item.item_id || "row"}`} className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs tracking-wide text-amber-200/70">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {[item.pickup_loc, item.dropoff_loc].filter(Boolean).join(" → ")}
                        {item.end_time ? `${item.pickup_loc || item.dropoff_loc ? " · " : ""}back by ${item.end_time}` : ""}
                      </p>
                    )}
                    {item.driver && (
                      <p data-testid={`trip-driver-${item.item_id || "row"}`} className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs tracking-wide text-emerald-200/80">
                        <User className="h-3 w-3 shrink-0" />
                        Your driver: <span className="font-semibold">{item.driver.name}</span>
                        {item.driver.phone && (
                          <span className="relative">
                            <button
                              data-testid={`trip-driver-phone-${item.item_id || "row"}`}
                              onClick={() => setCallFor(callFor === (item.item_id || "row") ? null : item.item_id || "row")}
                              className="underline decoration-emerald-500/40 underline-offset-2 transition-colors hover:text-emerald-100"
                            >
                              {item.driver.phone}
                            </button>
                            {callFor === (item.item_id || "row") && (
                              <span
                                data-testid={`trip-driver-callmenu-${item.item_id || "row"}`}
                                className="absolute left-0 top-full z-30 mt-1.5 flex gap-1.5 border border-emerald-500/40 bg-[#0c0c10] p-1.5 shadow-2xl"
                              >
                                <a
                                  data-testid={`trip-driver-call-${item.item_id || "row"}`}
                                  href={`tel:${item.driver.phone.replace(/\s/g, "")}`}
                                  onClick={() => setCallFor(null)}
                                  className="border border-emerald-500/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
                                >
                                  Call
                                </a>
                                <a
                                  data-testid={`trip-driver-whatsapp-${item.item_id || "row"}`}
                                  href={`https://wa.me/${item.driver.phone.replace(/[^\d]/g, "")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={() => setCallFor(null)}
                                  className="border border-emerald-500/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
                                >
                                  WhatsApp
                                </a>
                              </span>
                            )}
                          </span>
                        )}
                        {item.driver.email && (
                          <a data-testid={`trip-driver-email-${item.item_id || "row"}`} href={`mailto:${item.driver.email}`} className="underline decoration-emerald-500/40 underline-offset-2 transition-colors hover:text-emerald-100">
                            {item.driver.email}
                          </a>
                        )}
                      </p>
                    )}
                    {canEditHere && editing?.id === item.item_id && editing.field === "price" ? (
                      <input
                        autoFocus
                        data-testid={`demo-edit-price-${item.item_id}`}
                        defaultValue={item.price}
                        placeholder="e.g. 850 EUR"
                        onBlur={(e) => commitEdit(item.item_id, "price", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitEdit(item.item_id, "price", e.currentTarget.value);
                          if (e.key === "Escape") setEditing(null);
                        }}
                        className="mt-2 w-40 border border-amber-500/40 bg-[#0d0d12] px-2 py-1 font-luxe text-base tracking-[0.12em] text-amber-300 outline-none"
                      />
                    ) : (
                      shownPrice || (canEditHere && !bookedDirectly && !item.ops_info) ? (
                        <p
                          data-testid={day === item.day ? `trip-price-${item.item_id || "row"}` : undefined}
                          onClick={canEditHere ? () => setEditing({ id: item.item_id, field: "price" }) : undefined}
                          title={canEditHere ? "Click to edit the price" : undefined}
                          className={`mt-2 font-luxe text-base tracking-[0.12em] text-amber-300 ${canEditHere ? "cursor-text transition-colors hover:text-amber-100" : ""}`}
                        >
                          {shownPrice ? <DualPrice value={shownPrice} region={region} /> : (canEditHere ? "Add price" : "")}
                          {item.split_price && charged > 0 && day < endDay && shownPrice && (
                            <span className="ml-1 text-[0.65em] font-normal normal-case tracking-normal text-neutral-500">/ day</span>
                          )}
                        </p>
                      ) : null
                    )}
                    {bookedDirectly && day === item.day && (
                      <p data-testid={`booked-directly-${item.item_id || "row"}`} className="mt-2 font-serif text-sm italic text-neutral-400">
                        Client booked directly
                      </p>
                    )}
                    {(item.options || []).length > 0 && day === item.day && !bookedDirectly && (
                      <div data-testid={`menu-options-${item.item_id}`} className="mt-3 border-t border-amber-500/10 pt-3">
                        {onMenuPick && (
                          <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            {item.picked_option ? "Your restaurant choice — tap another to change" : "Restaurant options — tap the one you want"}
                          </p>
                        )}
                        <div className="space-y-1.5">
                          {item.options.map((o) => {
                            const picked = item.picked_option === o.option_id;
                            const check = (
                              <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center border transition-colors ${
                                  picked ? "border-amber-400 bg-amber-500 text-black" : "border-neutral-600"
                                }`}
                              >
                                {picked && <Check className="h-3 w-3" strokeWidth={3.5} />}
                              </span>
                            );
                            const menuLink = o.link ? (
                              <a
                                data-testid={`menu-link-${item.item_id}-${o.option_id}`}
                                href={o.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="shrink-0 border border-amber-500/40 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                              >
                                View menu
                              </a>
                            ) : null;
                            return onMenuPick ? (
                              <div
                                key={o.option_id}
                                className={`flex items-center gap-3 border px-3 py-2 transition-colors ${
                                  picked ? "border-amber-500/50 bg-amber-500/5" : "border-neutral-800/80 hover:border-amber-500/40"
                                }`}
                              >
                                <button
                                  type="button"
                                  data-testid={`menu-pick-${item.item_id}-${o.option_id}`}
                                  aria-pressed={picked}
                                  onClick={() => onMenuPick(item.item_id, picked ? "" : o.option_id)}
                                  className="flex flex-1 items-center gap-3 text-left"
                                >
                                  {check}
                                  <span className={`flex-1 text-sm ${picked ? "text-amber-100" : "text-neutral-300"}`}>{o.name}</span>
                                </button>
                                {menuLink}
                              </div>
                            ) : (
                              <div
                                key={o.option_id}
                                data-testid={`menu-option-${item.item_id}-${o.option_id}`}
                                className="flex items-center gap-3 px-3 py-1.5"
                              >
                                {check}
                                <span className={`flex-1 text-sm ${picked ? "text-amber-100" : "text-neutral-300"}`}>{o.name}</span>
                                {menuLink}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(item.full_alternative || (item.ops_full && item.alternative)) && day === item.day && (
                      <div className="mt-2">
                        <p data-testid={`full-alternative-${item.item_id || "row"}`} className="font-serif text-sm italic text-amber-200/80">
                          Fully booked — proposed alternative: {item.full_alternative || item.alternative}
                        </p>
                        {onAcceptAlt && item.item_id && (
                          <button
                            data-testid={`accept-alt-${item.item_id}`}
                            onClick={() => onAcceptAlt("item", item.item_id)}
                            className="mt-2 border border-amber-500/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                          >
                            Accept this alternative
                          </button>
                        )}
                      </div>
                    )}
                    {item.alt_accepted && day === item.day && (
                      <p data-testid={`alt-accepted-${item.item_id}`} className="mt-2 font-serif text-sm italic text-emerald-300/80">
                        Alternative accepted — thank you.
                      </p>
                    )}
                    {marked && (
                      <p
                        data-testid={`client-mark-${item.item_id}`}
                        className={`mt-3 text-xs uppercase tracking-[0.2em] ${
                          item.client_action === "removed" ? "text-red-400/90" : "text-amber-300/90"
                        }`}
                      >
                        {item.client_action === "removed"
                          ? "Client took this out"
                          : `Alternative requested${item.client_note ? ` — ${item.client_note}` : ""}`}
                      </p>
                    )}
                    {renderItemAction && <div className="mt-3">{renderItemAction(item)}</div>}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ))}
      {(() => {
        const totalIsk = sumIsk([
          ...(trip.hotels || []).filter((h) => !(h.booked_directly || h.ops_client_books || h.ops_person || h.ops_full)).map(hotelPriceForTotal),
          ...(trip.items || []).filter((x) => !(x.booked_directly || x.ops_client_books || x.ops_person || x.ops_full || x.ops_info)).map((x) => x.price),
          // a rental counts once — its total price, not once per displayed day
          ...(trip.rentals || []).map((r) => r.price),
        ]);
        if (totalIsk <= 0) return null;
        return (
          <div data-testid="trip-total" className="mt-10 flex flex-wrap items-baseline justify-between gap-2 border-t border-amber-500/25 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Trip total</p>
            <p className="font-luxe text-xl tracking-wide text-amber-100 sm:text-2xl">
              {fmtIsk(totalIsk)}{" "}
              <span className="text-sm font-normal normal-case tracking-normal text-neutral-500">
                {region === "eu" ? fmtEurApprox(totalIsk / ISK_PER_EUR) : fmtUsdApprox(totalIsk / ISK_PER_USD)}
              </span>
            </p>
          </div>
        );
      })()}
      <p data-testid="itinerary-disclaimer" className="mt-10 border-t border-neutral-800/60 pt-4 text-[9px] leading-relaxed tracking-[0.08em] text-neutral-600">
        There can be mistakes in pricing. This never happens, but if it does, we do not take responsibility if pricing is wrong.
        There is a 7 day cancellation policy on everything after it is confirmed with 0% refunded unless we can get money back from the tour operator.
      </p>
    </div>
  );
};
